/**
 * Utilities in this file have been copied over from @jira/src/packages/issue/format-time/src/isValidTimeString.tsx
 */
import isFinite from 'lodash/isFinite';

import secondsToTimeString from './secondsToTimeString';
import timeStringToSeconds from './timeStringToSeconds';
import type { TimeFormat, TimeUnit } from './types';

export type TimeTrackingConfig = {
	daysPerWeek: number;
	defaultUnit: TimeUnit;
	format?: TimeFormat;
	hoursPerDay: number;
	isTimeTrackingEnabled: boolean;
};

export const DEFAULT_TIME_TRACKING_UNIT_CLIENT = {
	minute: 'm',
	hour: 'h',
	day: 'd',
	week: 'w',
} as const;

export const getDefaultTimeTrackingUnit = (serverDefaultUnit: TimeUnit): string =>
	DEFAULT_TIME_TRACKING_UNIT_CLIENT[serverDefaultUnit] || DEFAULT_TIME_TRACKING_UNIT_CLIENT.minute;

const isNumeric = (num: string): boolean => num.length > 0 && !Number.isNaN(Number(num));

const isValidTimeFormat = (value: string): value is TimeFormat => {
	return value === 'pretty' || value === 'days' || value === 'hours';
};

const isValidTimeUnit = (value: string): value is TimeUnit => {
	return value === 'week' || value === 'day' || value === 'hour' || value === 'minute';
};

export const toTimeFormat = (
	value: string | null | undefined,
	defaultFormat: TimeFormat,
): TimeFormat => {
	if (!value) {
		return defaultFormat;
	}
	const lowercased = value.toLowerCase();
	if (isValidTimeFormat(lowercased)) {
		return lowercased;
	}
	return defaultFormat;
};

export const toTimeUnit = (value: string | null | undefined, defaultUnit: TimeUnit): TimeUnit => {
	if (!value) {
		return defaultUnit;
	}
	const lowercased = value.toLowerCase();
	if (isValidTimeUnit(lowercased)) {
		return lowercased;
	}
	return defaultUnit;
};

const isTimeStringValid = (timeString: string): boolean => {
	// We don't care about daysPerWeek or hoursPerDay - we only need to test that the value can be parsed.
	const result = timeStringToSeconds(1, 1)(timeString);
	return result !== null && isFinite(result);
};

export const formatTimeString =
	(config: TimeTrackingConfig) =>
	(timeValue: number | null): string =>
		secondsToTimeString(config.daysPerWeek, config.hoursPerDay)(timeValue || 0);

const appendTimeUnitIfMissing =
	(config: TimeTrackingConfig) =>
	(value: string): string => {
		let trimmedValue = value.trim();

		// If user doesn't type the valid format (m, h, d, or w), use default unit format set in the configuration.
		const isValidNumber = isNumeric(trimmedValue.replace(/,/g, '.'));
		if (isValidNumber) {
			const { defaultUnit } = config;
			trimmedValue = `${trimmedValue}${getDefaultTimeTrackingUnit(defaultUnit)}`;
		}

		return trimmedValue;
	};

export const isValidTimeString =
	(config: TimeTrackingConfig) =>
	(value: string): boolean => {
		const timeString = appendTimeUnitIfMissing(config)(value);
		return timeString === '' || isTimeStringValid(timeString);
	};

export const parseTimeString =
	(config: TimeTrackingConfig) =>
	(value: string): number | null => {
		const timeString = appendTimeUnitIfMissing(config)(value);

		if (!isValidTimeString(config)(timeString)) {
			return 0;
		}

		return timeStringToSeconds(config.daysPerWeek, config.hoursPerDay)(timeString);
	};
